import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Col, Row, Button, Spinner } from "reactstrap";
import api from "../../Axios";
import Swal from "sweetalert2";

const AddDocument = () => {
  const { id } = useParams();
  const history = useHistory();

  // Previews state - loaded from localStorage
  const [horoscopePreview, setHoroscopePreview] = useState(null);
  const [aadharPreviews, setAadharPreviews] = useState({
    front: null,
    back: null,
  });
  const [housePreviews, setHousePreviews] = useState({
    img1: null,
    img2: null,
    img3: null,
    img4: null,
  });

  // File states for upload
  const [horoscopeFile, setHoroscopeFile] = useState(null);
  const [aadharFiles, setAadharFiles] = useState({
    front: null,
    back: null,
  });
  const [houseFiles, setHouseFiles] = useState({
    img1: null,
    img2: null,
    img3: null,
    img4: null,
  });

  const [loading, setLoading] = useState({
    aadhar: false,
    houseImage: false,
    horoscopeImage: false,
  });

  const isMounted = useRef(true);

  // Load saved previews from localStorage on mount
  useEffect(() => {
    var savedData = localStorage.getItem("addDocumentPreviews_" + id);
    if (savedData) {
      try {
        var parsed = JSON.parse(savedData);
        if (parsed.horoscope) setHoroscopePreview(parsed.horoscope);
        if (parsed.aadhar) setAadharPreviews(parsed.aadhar);
        if (parsed.house) setHousePreviews(parsed.house);
      } catch (e) {
        console.error("Error loading saved previews:", e);
      }
    }

    return function () {
      isMounted.current = false;
    };
  }, [id]);

  // Save previews to localStorage whenever they change
  var saveToLocalStorage = function (horoscope, aadhar, house) {
    var dataToSave = {
      horoscope: horoscope,
      aadhar: aadhar,
      house: house,
    };
    localStorage.setItem("addDocumentPreviews_" + id, JSON.stringify(dataToSave));
  };

  // Convert file to base64 for localStorage
  var fileToBase64 = function (file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function () {
        resolve(reader.result);
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });
  };

  // Handle Horoscope Upload
  var handleHoroscopeUpload = function (e) {
    var file = e.target.files[0];
    if (!file) return;

    setHoroscopeFile(file);
    setLoading(function (prev) {
      return { ...prev, horoscopeImage: true };
    });

    var formData = new FormData();
    formData.append("image", file);

    api
      .post("/admin/userProfile/horoscopeImage?userId=" + id, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(function (res) {
        if (!isMounted.current) return;
        Swal.fire("Success", res.data.data || "Uploaded successfully!", "success");

        // Convert to base64 and save to localStorage
        fileToBase64(file).then(function (base64) {
          setHoroscopePreview(base64);
          saveToLocalStorage(base64, aadharPreviews, housePreviews);
        });
      })
      .catch(function () {
        Swal.fire("Error", "Failed to upload horoscope", "error");
      })
      .finally(function () {
        if (isMounted.current)
          setLoading(function (prev) {
            return { ...prev, horoscopeImage: false };
          });
      });
  };

  // Handle Aadhar Upload
  var handleAadharUpload = function () {
    if (!aadharFiles.front && !aadharFiles.back) {
      Swal.fire("Error", "Please select at least one Aadhar image", "error");
      return;
    }

    setLoading(function (prev) {
      return { ...prev, aadhar: true };
    });

    var formData = new FormData();
    if (aadharFiles.front) formData.append("images", aadharFiles.front);
    if (aadharFiles.back) formData.append("images", aadharFiles.back);

    api
      .post("/admin/userProfile/aadharImage?userId=" + id, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(function (res) {
        if (!isMounted.current) return;
        Swal.fire("Success", res.data.data || "Uploaded successfully!", "success");

        // Convert to base64 and save to localStorage
        var newPreviews = { ...aadharPreviews };
        var promises = [];

        if (aadharFiles.front) {
          promises.push(
            fileToBase64(aadharFiles.front).then(function (base64) {
              newPreviews.front = base64;
            })
          );
        }
        if (aadharFiles.back) {
          promises.push(
            fileToBase64(aadharFiles.back).then(function (base64) {
              newPreviews.back = base64;
            })
          );
        }

        Promise.all(promises).then(function () {
          setAadharPreviews(newPreviews);
          saveToLocalStorage(horoscopePreview, newPreviews, housePreviews);
        });

        setAadharFiles({ front: null, back: null });
      })
      .catch(function () {
        Swal.fire("Error", "Failed to upload Aadhar", "error");
      })
      .finally(function () {
        if (isMounted.current)
          setLoading(function (prev) {
            return { ...prev, aadhar: false };
          });
      });
  };

  // Handle House Image Upload
  var handleHouseUpload = function () {
    var files = Object.values(houseFiles).filter(function (f) {
      return f;
    });
    if (files.length === 0) {
      Swal.fire("Error", "Please select at least one house image", "error");
      return;
    }

    setLoading(function (prev) {
      return { ...prev, houseImage: true };
    });

    var formData = new FormData();
    files.forEach(function (file) {
      formData.append("images", file);
    });

    api
      .post("/admin/userProfile/houseImage?userId=" + id, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(function (res) {
        if (!isMounted.current) return;
        Swal.fire("Success", res.data.data || "Uploaded successfully!", "success");

        // Convert to base64 and save to localStorage
        var newPreviews = { ...housePreviews };
        var promises = [];

        Object.keys(houseFiles).forEach(function (key) {
          if (houseFiles[key]) {
            promises.push(
              fileToBase64(houseFiles[key]).then(function (base64) {
                newPreviews[key] = base64;
              })
            );
          }
        });

        Promise.all(promises).then(function () {
          setHousePreviews(newPreviews);
          saveToLocalStorage(horoscopePreview, aadharPreviews, newPreviews);
        });

        setHouseFiles({ img1: null, img2: null, img3: null, img4: null });
      })
      .catch(function () {
        Swal.fire("Error", "Failed to upload house images", "error");
      })
      .finally(function () {
        if (isMounted.current)
          setLoading(function (prev) {
            return { ...prev, houseImage: false };
          });
      });
  };

  // Handle Save - Clear localStorage and redirect to members
  var handleSave = function () {
    localStorage.removeItem("addDocumentPreviews_" + id);
    Swal.fire({
      icon: "success",
      title: "Success",
      text: "Documents saved successfully!",
      timer: 1500,
      showConfirmButton: false,
    }).then(function () {
      history.push("/allmembers");
    });
  };

  var removeHoroscope = function () {
    setHoroscopePreview(null);
    setHoroscopeFile(null);
    saveToLocalStorage(null, aadharPreviews, housePreviews);
  };

  return (
    <div className="content">
      <Row>
        <Col xs={12} md={12}>
          <div className="page-title">
            <div className="float-left">
              <h1 className="title">Upload Documents</h1>
            </div>
          </div>

          <div className="col-12">
            <section className="box">
              <div className="content-body">
                {/* Horoscope Image */}
                <div
                  className="form-outline mb-4 p-3"
                  style={{ border: "solid 1px", borderRadius: "5px" }}
                >
                  <label className="form-label">
                    <h5 style={{ paddingBottom: "5px" }}>Horoscope Image</h5>
                  </label>
                  <p>(File type: JPG, JPEG, PNG only)</p>
                  <div style={{ display: "flex", gap: "20px" }}>
                    <div
                      style={{
                        position: "relative",
                        width: "150px",
                        height: "150px",
                        border: "2px dashed #ddd",
                        borderRadius: "8px",
                        backgroundColor: "#f8f9fa",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {horoscopePreview ? (
                        <>
                          <img
                            src={horoscopePreview}
                            alt="Preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <button
                            type="button"
                            onClick={removeHoroscope}
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              background: "rgba(0,0,0,0.7)",
                              border: "none",
                              borderRadius: "50%",
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "white",
                              fontSize: "12px",
                            }}
                          >
                            &times;
                          </button>
                        </>
                      ) : (
                        <label
                          htmlFor="horoscope-input"
                          style={{
                            cursor: "pointer",
                            textAlign: "center",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "5px",
                            fontSize: "12px",
                          }}
                        >
                          <i
                            className="fa fa-upload"
                            style={{ fontSize: "24px", color: "#6c757d" }}
                          ></i>
                          <div>Upload</div>
                          <input
                            type="file"
                            id="horoscope-input"
                            accept=".jpg,.jpeg,.png"
                            style={{ display: "none" }}
                            onChange={handleHoroscopeUpload}
                          />
                        </label>
                      )}
                    </div>
                    {loading.horoscopeImage && (
                      <div className="mt-2 d-flex align-items-center">
                        <Spinner size="sm" color="primary" /> Uploading...
                      </div>
                    )}
                  </div>
                </div>

                {/* Aadhar Upload */}
                <div
                  className="form-outline mb-4 p-3"
                  style={{ border: "solid 1px", borderRadius: "5px" }}
                >
                  <label className="form-label">
                    <h5 style={{ paddingBottom: "5px" }}>
                      ID Proof - Aadhar Card
                    </h5>
                  </label>
                  <p>(Upload Front & Back - JPG, JPEG, PNG only)</p>
                  <div style={{ display: "flex", gap: "20px" }}>
                    {["front", "back"].map(function (key) {
                      return (
                        <div
                          key={key}
                          style={{
                            position: "relative",
                            width: "150px",
                            height: "150px",
                            border: "2px dashed #ddd",
                            borderRadius: "8px",
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          {aadharFiles[key] ? (
                            <>
                              <img
                                src={URL.createObjectURL(aadharFiles[key])}
                                alt={key + " preview"}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={function () {
                                  setAadharFiles(function (prev) {
                                    return { ...prev, [key]: null };
                                  });
                                }}
                                style={{
                                  position: "absolute",
                                  top: "5px",
                                  right: "5px",
                                  background: "rgba(0,0,0,0.7)",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: "24px",
                                  height: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  color: "white",
                                  fontSize: "12px",
                                }}
                              >
                                &times;
                              </button>
                            </>
                          ) : aadharPreviews[key] ? (
                            <>
                              <img
                                src={aadharPreviews[key]}
                                alt={key + " preview"}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <label
                                htmlFor={"aadhar-" + key + "-edit"}
                                style={{
                                  position: "absolute",
                                  bottom: "5px",
                                  right: "5px",
                                  background: "rgba(0,0,0,0.7)",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: "30px",
                                  height: "30px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  color: "white",
                                  fontSize: "14px",
                                }}
                              >
                                <i className="fa fa-pencil"></i>
                                <input
                                  type="file"
                                  id={"aadhar-" + key + "-edit"}
                                  accept=".jpg,.jpeg,.png"
                                  style={{ display: "none" }}
                                  onChange={function (e) {
                                    var file = e.target.files[0];
                                    if (!file) return;
                                    setAadharFiles(function (prev) {
                                      return { ...prev, [key]: file };
                                    });
                                  }}
                                />
                              </label>
                            </>
                          ) : (
                            <label
                              htmlFor={"aadhar-" + key}
                              style={{
                                cursor: "pointer",
                                textAlign: "center",
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "5px",
                                fontSize: "12px",
                              }}
                            >
                              <i
                                className="fa fa-upload"
                                style={{ fontSize: "24px", color: "#6c757d" }}
                              ></i>
                              <div>{key === "front" ? "Front" : "Back"}</div>
                              <input
                                type="file"
                                id={"aadhar-" + key}
                                accept=".jpg,.jpeg,.png"
                                style={{ display: "none" }}
                                onChange={function (e) {
                                  var file = e.target.files[0];
                                  if (!file) return;
                                  setAadharFiles(function (prev) {
                                    return { ...prev, [key]: file };
                                  });
                                }}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Button
                    color="primary"
                    onClick={handleAadharUpload}
                    disabled={loading.aadhar}
                    className="mt-3"
                  >
                    {loading.aadhar ? <Spinner size="sm" /> : "Upload Aadhar"}
                  </Button>
                </div>

                {/* House Images */}
                <div
                  className="form-outline mb-4 p-3"
                  style={{ border: "solid 1px", borderRadius: "5px" }}
                >
                  <label className="form-label">
                    <h5 style={{ paddingBottom: "5px" }}>House Images</h5>
                  </label>
                  <p>(Upload 4 images separately - JPG, JPEG, PNG only)</p>
                  <div className="d-flex flex-wrap gap-3">
                    {["img1", "img2", "img3", "img4"].map(function (key, index) {
                      return (
                        <div
                          key={key}
                          style={{
                            position: "relative",
                            width: "150px",
                            height: "150px",
                            border: "2px dashed #ddd",
                            borderRadius: "8px",
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          {houseFiles[key] ? (
                            <>
                              <img
                                src={URL.createObjectURL(houseFiles[key])}
                                alt={"House " + (index + 1)}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                onClick={function () {
                                  setHouseFiles(function (prev) {
                                    return { ...prev, [key]: null };
                                  });
                                }}
                                style={{
                                  position: "absolute",
                                  top: "5px",
                                  right: "5px",
                                  background: "rgba(0,0,0,0.7)",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: "24px",
                                  height: "24px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  color: "white",
                                  fontSize: "12px",
                                }}
                              >
                                &times;
                              </button>
                            </>
                          ) : housePreviews[key] ? (
                            <>
                              <img
                                src={housePreviews[key]}
                                alt={"House " + (index + 1)}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <label
                                htmlFor={"house-" + key + "-edit"}
                                style={{
                                  position: "absolute",
                                  bottom: "5px",
                                  right: "5px",
                                  background: "rgba(0,0,0,0.7)",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: "30px",
                                  height: "30px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  color: "white",
                                  fontSize: "14px",
                                }}
                              >
                                <i className="fa fa-pencil"></i>
                                <input
                                  type="file"
                                  id={"house-" + key + "-edit"}
                                  accept=".jpg,.jpeg,.png"
                                  style={{ display: "none" }}
                                  onChange={function (e) {
                                    var file = e.target.files[0];
                                    if (!file) return;
                                    setHouseFiles(function (prev) {
                                      return { ...prev, [key]: file };
                                    });
                                  }}
                                />
                              </label>
                            </>
                          ) : (
                            <label
                              htmlFor={"house-" + key}
                              style={{
                                cursor: "pointer",
                                textAlign: "center",
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "5px",
                                fontSize: "12px",
                              }}
                            >
                              <i
                                className="fa fa-upload"
                                style={{ fontSize: "24px", color: "#6c757d" }}
                              ></i>
                              <div>Image {index + 1}</div>
                              <input
                                type="file"
                                id={"house-" + key}
                                accept=".jpg,.jpeg,.png"
                                style={{ display: "none" }}
                                onChange={function (e) {
                                  var file = e.target.files[0];
                                  if (!file) return;
                                  setHouseFiles(function (prev) {
                                    return { ...prev, [key]: file };
                                  });
                                }}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Button
                    color="primary"
                    onClick={handleHouseUpload}
                    disabled={loading.houseImage}
                    className="mt-2"
                  >
                    {loading.houseImage ? (
                      <Spinner size="sm" />
                    ) : (
                      "Upload House Images"
                    )}
                  </Button>
                </div>

                {/* Save Button */}
                <div className="mt-4">
                  <Button
                    style={{
                      background: "#000000",
                      border: "none",
                      padding: "12px 40px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                    onClick={handleSave}
                  >
                    <i className="fa fa-save mr-2"></i>
                    Save
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AddDocument;
